import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Utensils, ChefHat, Settings, ClipboardList, Mic, BarChart3, QrCode, Radio, ShieldAlert, Sparkles, Pencil, Flame, Check, X, Users, Building2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { CATEGORIES, ALLERGENS } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import VoiceControl from "../components/VoiceControl";
import DishConfirmCard from "../components/DishConfirmCard";
import DishCard from "../components/DishCard";
import QRPanel from "../components/QRPanel";
import AnalyticsPanel from "../components/AnalyticsPanel";
import OrdersPanel from "../components/OrdersPanel";
import OwnerChat, { OwnerReviewConfig } from "../components/OwnerChat";
import PhotoMenuUpload from "../components/PhotoMenuUpload";
import ContactsPanel from "../components/ContactsPanel";
import BusinessProfilePanel from "../components/BusinessProfilePanel";

const TABS = [
  { id: "orders", label: "Ordini", icon: ClipboardList, testid: "tab-orders" },
  { id: "menu", label: "Menu vocale", icon: Mic, testid: "tab-menu" },
  { id: "daily", label: "Fuori Menu", icon: Flame, testid: "tab-daily" },
  { id: "analytics", label: "Analitiche", icon: BarChart3, testid: "tab-analytics" },
  { id: "contacts", label: "Contatti", icon: Users, testid: "tab-contacts" },
  { id: "qr", label: "QR Code", icon: QrCode, testid: "tab-qr" },
  { id: "chat", label: "Chat & Promo", icon: Radio, testid: "tab-chat" },
  { id: "boost", label: "Boost Recensioni", icon: Star, testid: "tab-boost" },
  { id: "business", label: "Azienda", icon: Building2, testid: "tab-business" },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [category, setCategory] = useState("all");
  const [allergenFilters, setAllergenFilters] = useState([]);
  const [parsed, setParsed] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [tab, setTab] = useState("orders");
  const [me, setMe] = useState(null);
  const [renameCat, setRenameCat] = useState(null); // { old, new }

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/auth/me"); setMe(data); } catch { /* plan info unavailable */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [r, d] = await Promise.all([api.get("/restaurant"), api.get("/dishes")]);
        setRestaurant(r.data); setDishes(d.data);
        // Default tab: Ordini se ordering_enabled, altrimenti Menu
        if (r.data?.ordering_enabled === false) setTab("menu");
      } catch { toast.error("Errore caricamento dati"); }
    })();
  }, []);

  const filteredDishes = useMemo(() => {
    let list = dishes.filter((d) => !d.is_daily_menu);
    if (category !== "all") list = list.filter((d) => d.category === category);
    if (allergenFilters.length) list = list.filter((d) => !d.allergens?.some((a) => allergenFilters.includes(a)));
    return list;
  }, [dishes, category, allergenFilters]);

  const availableCategories = useMemo(() => {
    const custom = restaurant?.custom_categories || [];
    return [...CATEGORIES, ...custom.filter((c) => !CATEGORIES.includes(c))];
  }, [restaurant]);

  const stats = useMemo(() => ({
    total: dishes.length,
    active: dishes.filter((d) => d.available).length,
    avgPrice: dishes.length ? (dishes.reduce((s, d) => s + Number(d.price || 0), 0) / dishes.length).toFixed(2) : "0.00",
  }), [dishes]);

  const handleParsed = (data, tr) => { setParsed(data); setTranscript(tr); };

  const handleConfirm = async (payload) => {
    const { matched, intent, ...form } = payload;
    // Force is_daily_menu based on active tab (parallel menus) — only on create/add
    if (intent === "add" || !intent) form.is_daily_menu = tab === "daily";
    try {
      if (intent === "delete" && matched) {
        await api.delete(`/dishes/${matched.id}`);
        setDishes((prev) => prev.filter((d) => d.id !== matched.id));
        toast.success(`"${matched.name}" eliminato`);
      } else if (intent === "toggle_special" && matched) {
        const { data } = await api.post(`/dishes/${matched.id}/toggle-special`);
        setDishes((prev) => prev.map((d) => d.id === data.id ? data : d));
        toast.success(data.is_daily_special ? `"${data.name}" nel Menu del Giorno 🔥` : `"${data.name}" rimosso dal Menu del Giorno`);
      } else if ((intent === "update_price" || intent === "update") && matched) {
        const { data } = await api.put(`/dishes/${matched.id}`, form);
        setDishes((prev) => prev.map((d) => d.id === data.id ? data : d));
        toast.success(`"${data.name}" aggiornato`);
      } else {
        const { data } = await api.post("/dishes", form);
        setDishes((prev) => [...prev, { ...data, _generating: true }]);
        toast.success(form.is_daily_menu ? `"${data.name}" aggiunto al Menu del Giorno 🍽` : `"${data.name}" aggiunto al menu`);
        (async () => {
          try {
            const { data: img } = await api.post("/image/generate", {
              prompt: `${data.name}. ${data.description || ""} Ingredienti: ${(data.ingredients||[]).join(", ")}`,
              dish_id: data.id,
            });
            setDishes((prev) => prev.map((d) => d.id === data.id ? { ...d, image_url: img.image_url, _generating: false } : d));
            toast.success(`Foto AI generata per "${data.name}"`);
          } catch (err) {
            setDishes((prev) => prev.map((d) => d.id === data.id ? { ...d, _generating: false } : d));
            toast.error(`Foto AI non generata: ${err?.response?.data?.detail || "riprova manualmente"}`);
          }
        })();
      }
      setParsed(null); setTranscript("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const toggleAllergenFilter = (a) => setAllergenFilters((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  const dailyMenuDishes = useMemo(() => dishes.filter((d) => d.is_daily_menu), [dishes]);
  const regularDishes = useMemo(() => dishes.filter((d) => !d.is_daily_menu), [dishes]);

  const filteredRegularDishes = useMemo(() => {
    let list = regularDishes;
    if (category !== "all") list = list.filter((d) => d.category === category);
    if (allergenFilters.length) list = list.filter((d) => !d.allergens?.some((a) => allergenFilters.includes(a)));
    return list;
  }, [regularDishes, category, allergenFilters]);

  const doRenameCategory = async () => {
    const oldName = renameCat.old.trim();
    const newName = (renameCat.new || "").trim();
    if (!newName || newName === oldName) { setRenameCat(null); return; }
    try {
      await api.put("/categories", { old_name: oldName, new_name: newName });
      const { data } = await api.get("/restaurant");
      setRestaurant(data);
      const { data: d } = await api.get("/dishes");
      setDishes(d);
      if (category === oldName) setCategory(newName);
      toast.success(`Categoria "${oldName}" rinominata in "${newName}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    } finally { setRenameCat(null); }
  };

  // Plan gate
  const notActivated = me && (!me.activated || me.plan === "pending");
  const planLabels = { pending: "In attesa attivazione", trial: "Trial 3gg", full: "Full · 100€/mese", voice_only: "Menu AI · 60€/mese", expired: "Scaduto" };
  const planColors = { pending: "#6B625B", trial: "#D99B26", full: "#3B503A", voice_only: "#C85A32", expired: "#8B0000" };
  const daysLeft = me?.trial_ends_at || me?.subscription_ends_at ? Math.max(0, Math.ceil((new Date(me.trial_ends_at || me.subscription_ends_at) - new Date())/86400000)) : null;

  return (
    <div className="min-h-screen bg-[#FBF9F5] grain pb-24 md:pb-8">
      {/* Header */}
      <header className="border-b border-[#E8E2D5] bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#D99B26] to-[#C85A32] grid place-items-center text-white font-black flex-shrink-0">V</div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-base sm:text-lg leading-tight truncate">{restaurant?.name || "Il tuo locale"}</div>
              <div className="text-[11px] text-[#6B625B]">VOICEMENU · Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 text-sm text-[#6B625B]"><ChefHat size={16}/> {user?.name}</div>
            <button data-testid="settings-btn" onClick={() => navigate("/onboarding")} className="w-9 h-9 rounded-full border border-[#E8E2D5] grid place-items-center hover:bg-[#F0EBE1]"><Settings size={16}/></button>
            <button data-testid="logout-btn" onClick={doLogout} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#231F1D] hover:text-[#C85A32]"><LogOut size={16}/><span className="hidden sm:inline">Esci</span></button>
          </div>
        </div>

        {/* Desktop tab bar */}
        <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-8 pb-2">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const Icon = t.icon; const active = tab === t.id;
              return (
                <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${active ? "bg-[#231F1D] text-white shadow" : "text-[#6B625B] hover:bg-[#F0EBE1]"}`}>
                  <Icon size={15}/> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-5 sm:py-8">
        {notActivated && (
          <div data-testid="pending-banner" className="mb-6 rounded-3xl border-2 border-[#D99B26] bg-gradient-to-br from-[#FEF3D6] to-[#FDECE4] p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D99B26] text-white grid place-items-center flex-shrink-0"><ShieldAlert size={22}/></div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-black text-[#8C580C]">Account in attesa</div>
              <h3 className="font-serif-display text-2xl font-bold text-[#231F1D] mt-0.5">Attiva il tuo piano</h3>
              <p className="text-sm text-[#6B625B] mt-1">Ricevi <b>3 giorni gratis</b> di prova. Nessun addebito durante il trial — puoi disdire quando vuoi.</p>
              <button data-testid="activate-plan-btn" onClick={() => navigate("/choose-plan")} className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white font-black shadow-lg hover:brightness-110">
                <Sparkles size={14}/> Scegli il piano →
              </button>
            </div>
          </div>
        )}

        {me?.activated && (
          <div data-testid="plan-badge-bar" className="mb-5 flex items-center justify-between gap-3 rounded-2xl bg-white border border-[#E8E2D5] px-4 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles size={14} className="text-[#C85A32]"/>
              <span className="text-xs uppercase tracking-widest font-bold" style={{ color: planColors[me.plan] }}>{planLabels[me.plan] || me.plan}</span>
              {daysLeft != null && <span className="text-[11px] text-[#6B625B]">· {daysLeft} giorni</span>}
              {me.in_trial && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D99B26] text-white font-bold">Trial · tutto attivo</span>}
            </div>
            {me.plan === "voice_only" && <span className="text-[11px] text-[#6B625B] hidden sm:block">Ordini/Chat/Boost Recensioni: passa a All In · 3€/giorno</span>}
            {me.activated && me.plan !== "pending" && me.plan !== "expired" && (
              <button
                data-testid="cancel-subscription-btn"
                onClick={async () => {
                  if (!window.confirm("Sei sicuro di voler disdire l'abbonamento? Perderai l'accesso alle funzioni a pagamento immediatamente.")) return;
                  try {
                    await api.post("/subscription/cancel");
                    toast.success("Abbonamento disdetto");
                    setTimeout(() => window.location.reload(), 800);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                }}
                className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-[#8B0000]/40 text-[#8B0000] hover:bg-[#FDECE4]"
              >
                Disdici piano
              </button>
            )}
          </div>
        )}

        {/* Stats always visible */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
          {[
            { label: "Piatti", value: stats.total, icon: <Utensils size={16}/> },
            { label: "Disponibili", value: stats.active, icon: <ChefHat size={16}/> },
            { label: "Prezzo medio", value: `€ ${stats.avgPrice}`, icon: <BarChart3 size={16}/> },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E8E2D5] p-3 sm:p-4">
              <div className="text-[#C85A32]">{s.icon}</div>
              <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-[#6B625B] font-semibold">{s.label}</div>
              <div className="font-serif-display text-xl sm:text-2xl font-bold mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tab content */}
        {tab === "orders" && (
          <div className="space-y-6"><OrdersPanel restaurant={restaurant}/></div>
        )}

        {tab === "menu" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 flex flex-col gap-6">
              <VoiceControl onParsed={handleParsed}/>
              <PhotoMenuUpload onImported={(created) => setDishes((prev) => [...prev, ...created])} onCategoriesChanged={async () => { try { const { data } = await api.get("/restaurant"); setRestaurant(data); } catch {} }}/>
              {parsed && (
                <DishConfirmCard parsed={parsed} transcript={transcript} existingDishes={dishes}
                  onCancel={() => { setParsed(null); setTranscript(""); }} onConfirm={handleConfirm}/>
              )}
            </div>
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div className="bg-white rounded-3xl border border-[#E8E2D5] p-5 sm:p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h3 className="font-serif-display text-xl sm:text-2xl font-bold">Il tuo menu</h3>
                  <div className="text-sm text-[#6B625B]">{filteredDishes.length} piatti</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  <button data-testid="dish-category-tab-all" onClick={() => setCategory("all")} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${category === "all" ? "bg-white text-[#C85A32] shadow" : "bg-[#F0EBE1] text-[#6B625B]"}`}>Tutti</button>
                  {availableCategories.map((c) => (
                    <div key={c} className={`inline-flex items-center rounded-full ${category === c ? "bg-white text-[#C85A32] shadow" : "bg-[#F0EBE1] text-[#6B625B]"}`}>
                      <button data-testid={`dish-category-tab-${c}`} onClick={() => setCategory(c)} className="px-3 py-1.5 text-sm font-semibold">{c}</button>
                      <button data-testid={`rename-category-${c}`} onClick={() => setRenameCat({ old: c, new: c })} title="Rinomina categoria" className={`w-6 h-6 rounded-full mr-1 grid place-items-center hover:bg-black/10 ${category === c ? "text-[#C85A32]" : "text-[#6B625B]"}`}>
                        <Pencil size={11}/>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <span className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold mr-1">Escludi allergene:</span>
                  {ALLERGENS.slice(0, 6).map((a) => {
                    const active = allergenFilters.includes(a);
                    return (
                      <button key={a} data-testid={`filter-allergen-${a}`} onClick={() => toggleAllergenFilter(a)} className={`text-xs px-3 py-1 rounded-full border transition-all ${active ? "bg-[#3B503A] text-white border-[#3B503A]" : "bg-white text-[#6B625B] border-[#E8E2D5]"}`}>{a}</button>
                    );
                  })}
                </div>
              </div>
              {filteredRegularDishes.length === 0 ? (
                <div className="bg-white rounded-3xl border border-dashed border-[#E8E2D5] p-10 text-center">
                  <Utensils className="mx-auto text-[#C85A32]" size={30}/>
                  <h4 className="mt-3 font-serif-display text-xl font-bold">Nessun piatto ancora</h4>
                  <p className="text-sm text-[#6B625B] mt-1">Usa la voce a sinistra: "Aggiungi Bruschetta al pomodoro a 6 euro negli Antipasti"</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredRegularDishes.map((d) => (
                    <DishCard key={d.id} dish={d} availableCategories={availableCategories} onUpdated={(u) => setDishes((prev) => prev.map((x) => x.id === u.id ? u : x))} onDeleted={(id) => setDishes((prev) => prev.filter((x) => x.id !== id))}/>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "daily" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 flex flex-col gap-6">
              <div data-testid="daily-menu-banner" className="rounded-3xl bg-gradient-to-br from-[#D99B26] via-[#E05328] to-[#C85A32] text-white p-5 sm:p-6 shadow-xl shadow-[#C85A32]/30">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/25 backdrop-blur text-[10px] font-black uppercase tracking-widest">
                  <Flame size={11}/> Menu parallelo · fuori menu
                </div>
                <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl font-black leading-tight">Crea Fuori Menu</h3>
                <p className="mt-2 text-sm text-white/95 leading-relaxed">Piatti freschi arrivati oggi, specialità del giorno, fuori menu temporanei. Questa sezione è <b>indipendente</b> dal menu fisso — coesistono senza sovrapporsi.</p>
                <div className="mt-3 text-[11px] text-white/85 flex items-center gap-1.5"><Sparkles size={12}/> I piatti creati qui appaiono solo nella sezione "Menu del Giorno" del menu pubblico.</div>
              </div>
              <VoiceControl onParsed={handleParsed}/>
              <PhotoMenuUpload dailyMode onImported={(created) => setDishes((prev) => [...prev, ...created])} onCategoriesChanged={async () => { try { const { data } = await api.get("/restaurant"); setRestaurant(data); } catch {} }}/>
              {parsed && (
                <DishConfirmCard parsed={parsed} transcript={transcript} existingDishes={dishes}
                  onCancel={() => { setParsed(null); setTranscript(""); }} onConfirm={handleConfirm}/>
              )}
            </div>
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div className="bg-white rounded-3xl border-2 border-[#D99B26]/40 p-5 sm:p-6">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D99B26] to-[#C85A32] grid place-items-center text-white shadow"><Flame size={17}/></div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-black text-[#8C580C]">Menu del Giorno · Fuori Menu</div>
                    <h3 className="font-serif-display text-xl sm:text-2xl font-bold leading-tight">{dailyMenuDishes.length} piatt{dailyMenuDishes.length === 1 ? "o" : "i"} attiv{dailyMenuDishes.length === 1 ? "o" : "i"}</h3>
                  </div>
                </div>
                <p className="text-xs text-[#6B625B] mt-2">I clienti vedono questo elenco in una sezione dedicata e separata dal menu fisso.</p>
              </div>
              {dailyMenuDishes.length === 0 ? (
                <div data-testid="daily-menu-empty" className="bg-white rounded-3xl border border-dashed border-[#D99B26]/60 p-10 text-center">
                  <Flame className="mx-auto text-[#C85A32]" size={30}/>
                  <h4 className="mt-3 font-serif-display text-xl font-bold">Nessun fuori menu oggi</h4>
                  <p className="text-sm text-[#6B625B] mt-1">Usa la voce o carica una foto per aggiungere piatti solo alla sezione "Menu del Giorno".</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dailyMenuDishes.map((d) => (
                    <DishCard key={d.id} dish={d} availableCategories={availableCategories} onUpdated={(u) => setDishes((prev) => prev.map((x) => x.id === u.id ? u : x))} onDeleted={(id) => setDishes((prev) => prev.filter((x) => x.id !== id))}/>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "analytics" && <div className="max-w-3xl mx-auto"><AnalyticsPanel/></div>}
        {tab === "contacts" && <div className="max-w-3xl mx-auto"><ContactsPanel/></div>}
        {tab === "qr" && <div className="max-w-md mx-auto"><QRPanel restaurant={restaurant}/></div>}
        {tab === "chat" && <div className="max-w-2xl mx-auto"><OwnerChat restaurant={restaurant} me={me}/></div>}
        {tab === "boost" && <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-[#E8E2D5] shadow-sm overflow-hidden"><OwnerReviewConfig/></div>}
        {tab === "business" && <div className="max-w-3xl mx-auto"><BusinessProfilePanel restaurantSlug={restaurant?.slug}/></div>}
      </main>

      {/* Mobile sticky bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#E8E2D5] bg-white/95 backdrop-blur">
        <div className="max-w-md mx-auto flex items-stretch">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.id;
            return (
              <button key={t.id} data-testid={`mob-${t.testid}`} onClick={() => setTab(t.id)} className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 ${active ? "text-[#C85A32]" : "text-[#6B625B]"}`}>
                <Icon size={19}/>
                <span className="text-[9px] font-bold uppercase tracking-wider">{t.label.split(" ")[0]}</span>
                {active && <span className="absolute bottom-0 w-6 h-0.5 rounded-t bg-[#C85A32]"/>}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Rename category modal */}
      {renameCat && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur grid place-items-center p-4" data-testid="rename-category-modal">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 fade-in-up">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#C85A32]">Rinomina categoria</div>
            <h3 className="font-serif-display text-2xl font-bold mt-1 text-[#231F1D]">{renameCat.old}</h3>
            <p className="text-sm text-[#6B625B] mt-1">Il nuovo nome sarà aggiornato ovunque, anche sui piatti.</p>
            <input
              autoFocus
              data-testid="rename-category-input"
              value={renameCat.new}
              onChange={(e) => setRenameCat({ ...renameCat, new: e.target.value })}
              placeholder="Nuovo nome"
              className="mt-4 w-full rounded-full border border-[#E8E2D5] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
              onKeyDown={(e) => { if (e.key === "Enter") doRenameCategory(); }}
            />
            <div className="mt-4 flex gap-2">
              <button data-testid="rename-cancel" onClick={() => setRenameCat(null)} className="flex-1 py-2.5 rounded-full border border-[#E8E2D5] text-[#231F1D] font-semibold inline-flex items-center justify-center gap-1"><X size={14}/> Annulla</button>
              <button data-testid="rename-confirm" onClick={doRenameCategory} className="flex-1 py-2.5 rounded-full bg-[#3B503A] text-white font-semibold inline-flex items-center justify-center gap-1"><Check size={14}/> Rinomina</button>
            </div>
          </div>
        </div>
      )}

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}
